/**
 * Audience Engine — who the design speaks to.
 *
 * An audience profile carries the creative constraints a real art director
 * starts from: which moods and brand styles suit this audience, how much
 * visual energy it tolerates, and how it typically reads promotional
 * material. Profiles also name the Template Factory category they map to,
 * so the adapter never guesses.
 */

const profile = (def) => ({ heroImageImportance: 'high', ...def });

export const AUDIENCES = {
  restaurant: profile({
    id: 'restaurant', name: 'Restaurant',
    moods: ['warm', 'premium', 'energetic'],
    brandStyles: ['modern', 'elegant', 'bold'],
    energy: 0.6, readingHabit: 'image-first',
    factoryCategory: 'restaurant',
  }),
  cafe: profile({
    id: 'cafe', name: 'Cafe',
    moods: ['warm', 'fresh', 'playful'],
    brandStyles: ['creative', 'minimal', 'street'],
    energy: 0.5, readingHabit: 'image-first',
    factoryCategory: 'food',
  }),
  gym: profile({
    id: 'gym', name: 'Gym',
    moods: ['energetic', 'bold', 'fresh'],
    brandStyles: ['bold', 'street', 'modern'],
    energy: 0.9, readingHabit: 'headline-first',
    factoryCategory: 'gym',
  }),
  medical: profile({
    id: 'medical', name: 'Medical',
    moods: ['fresh', 'healthy', 'minimal'],
    brandStyles: ['corporate', 'minimal', 'modern'],
    energy: 0.25, readingHabit: 'text-first',
    heroImageImportance: 'medium',
    factoryCategory: 'medical',
  }),
  salon: profile({
    id: 'salon', name: 'Salon',
    moods: ['elegant', 'premium', 'warm'],
    brandStyles: ['elegant', 'luxury', 'minimal'],
    energy: 0.45, readingHabit: 'image-first',
    factoryCategory: 'salon',
  }),
  'real-estate': profile({
    id: 'real-estate', name: 'Real Estate',
    moods: ['premium', 'minimal', 'elegant'],
    brandStyles: ['corporate', 'luxury', 'editorial'],
    energy: 0.35, readingHabit: 'image-first',
    factoryCategory: 'real-estate',
  }),
  corporate: profile({
    id: 'corporate', name: 'Corporate',
    moods: ['minimal', 'premium', 'bold'],
    brandStyles: ['corporate', 'modern', 'editorial'],
    energy: 0.35, readingHabit: 'text-first',
    heroImageImportance: 'medium',
    factoryCategory: 'corporate',
  }),
  education: profile({
    id: 'education', name: 'Education',
    moods: ['fresh', 'playful', 'bold'],
    brandStyles: ['modern', 'creative', 'corporate'],
    energy: 0.55, readingHabit: 'headline-first',
    heroImageImportance: 'medium',
    factoryCategory: 'education',
  }),
  fashion: profile({
    id: 'fashion', name: 'Fashion',
    moods: ['elegant', 'bold', 'minimal'],
    brandStyles: ['editorial', 'luxury', 'street'],
    energy: 0.6, readingHabit: 'image-first',
    factoryCategory: 'fashion',
  }),
  festival: profile({
    id: 'festival', name: 'Festival',
    moods: ['energetic', 'playful', 'bold'],
    brandStyles: ['creative', 'bold', 'street'],
    energy: 0.95, readingHabit: 'headline-first',
    factoryCategory: 'festival',
  }),
  travel: profile({
    id: 'travel', name: 'Travel',
    moods: ['fresh', 'energetic', 'premium'],
    brandStyles: ['modern', 'creative', 'editorial'],
    energy: 0.65, readingHabit: 'image-first',
    factoryCategory: 'travel',
  }),
  kids: profile({
    id: 'kids', name: 'Kids',
    moods: ['playful', 'energetic', 'warm'],
    brandStyles: ['creative', 'bold', 'modern'],
    energy: 0.85, readingHabit: 'image-first',
    factoryCategory: 'events',
  }),
  luxury: profile({
    id: 'luxury', name: 'Luxury',
    moods: ['luxury', 'premium', 'elegant'],
    brandStyles: ['luxury', 'premium', 'editorial'],
    energy: 0.2, readingHabit: 'image-first',
    factoryCategory: 'fashion',
  }),
};

export const listAudiences = () =>
  Object.values(AUDIENCES).map(({ id, name }) => ({ id, name }));

export const getAudience = (id) => {
  const audience = AUDIENCES[id];
  if (!audience) throw new Error(`Unknown audience "${id}"`);
  return audience;
};
